import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertTriangle, CheckCircle, Loader2, RotateCcw, UserCheck } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { approveProposalReview, rejectProposalReview } from "@/lib/proposalsApi";
import { lookupUsersByRoles } from "@/lib/usersApi";

interface OperationsUser {
  id: string;
  full_name: string;
  email: string;
}

interface ManagerReviewPanelProps {
  proposalId: string;
  proposalStatus: string | null | undefined;
  onStatusChange: () => void;
}

export const ManagerReviewPanel: React.FC<ManagerReviewPanelProps> = ({
  proposalId,
  proposalStatus,
  onStatusChange,
}) => {
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [operationsUsers, setOperationsUsers] = useState<OperationsUser[]>([]);
  const [selectedOperationsUser, setSelectedOperationsUser] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const { toast } = useToast();

  const isVisible = proposalStatus === "paid_internal_review";

  useEffect(() => {
    if (showApproveModal) {
      loadOperationsUsers();
    }
  }, [showApproveModal]);

  const loadOperationsUsers = async () => {
    try {
      setLoadingUsers(true);
      const users = await lookupUsersByRoles(["operations_staff", "ceo", "general_manager"]);
      setOperationsUsers(
        (users || []).map((user) => ({
          id: user.id,
          full_name: user.full_name,
          email: user.email,
        }))
      );
    } catch (error) {
      console.error("Error loading operations users:", error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast({
        title: "Rejection reason required",
        description: "Please provide a reason for returning this proposal to Sales.",
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);
      await rejectProposalReview(proposalId, rejectionReason);

      toast({
        title: "Proposal returned to Sales",
        description: "The proposal has been sent back for fixes.",
      });

      setShowRejectModal(false);
      setRejectionReason("");
      onStatusChange();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedOperationsUser) {
      toast({
        title: "Operations owner required",
        description: "Please select an operations team member to handle the booking.",
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);
      await approveProposalReview(proposalId, selectedOperationsUser);

      toast({
        title: "Booking Package Created",
        description: "The proposal has been approved and booking package is ready for operations.",
      });

      setShowApproveModal(false);
      setSelectedOperationsUser("");
      onStatusChange();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isVisible) return null;

  return (
    <>
      <Card className="border-amber-500/50 bg-amber-50/30">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <UserCheck className="h-5 w-5 text-amber-600" />
            Manager Review Required
          </CardTitle>
          <CardDescription>
            Review the proposal and invoice before creating the booking package for operations.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex gap-3">
          <Button
            variant="outline"
            className="border-red-300 text-red-700 hover:bg-red-50"
            onClick={() => setShowRejectModal(true)}
          >
            <RotateCcw className="mr-2 h-4 w-4" />
            Return to Sales – Needs Fix
          </Button>
          <Button
            className="bg-emerald-600 hover:bg-emerald-700"
            onClick={() => setShowApproveModal(true)}
          >
            <CheckCircle className="mr-2 h-4 w-4" />
            Approve & Create Booking Package
          </Button>
        </CardContent>
      </Card>

      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Return to Sales
            </DialogTitle>
            <DialogDescription>
              Please provide a reason for returning this proposal to the Sales team.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="rejection-reason">
                Rejection Reason <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="rejection-reason"
                placeholder="Describe what needs to be fixed..."
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectModal(false)} disabled={loading}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={loading || !rejectionReason.trim()}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                "Return to Sales"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showApproveModal} onOpenChange={setShowApproveModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-emerald-500" />
              Approve & Create Booking Package
            </DialogTitle>
            <DialogDescription>
              Select an operations team member who will handle the bookings for this proposal.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="operations-select">
                Assign to Operations <span className="text-destructive">*</span>
              </Label>
              <Select
                value={selectedOperationsUser}
                onValueChange={setSelectedOperationsUser}
                disabled={loadingUsers}
              >
                <SelectTrigger id="operations-select">
                  <SelectValue placeholder={loadingUsers ? "Loading..." : "Select operations user"} />
                </SelectTrigger>
                <SelectContent>
                  {operationsUsers.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      <div className="flex flex-col">
                        <span className="font-medium">{user.full_name}</span>
                        <span className="text-xs text-muted-foreground">{user.email}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApproveModal(false)} disabled={loading}>
              Cancel
            </Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={handleApprove}
              disabled={loading || !selectedOperationsUser}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                "Approve & Create"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ManagerReviewPanel;
